import { env } from '@/core/env';

/**
 * Live business card detection for the camera preview.
 *
 * Each sampled frame is cropped to what the preview actually shows
 * (`object-cover`), downscaled, and searched for a card-shaped quadrilateral:
 * four near-axis-aligned straight edges where the card is consistently
 * brighter (or darker) than its surroundings on every side, with a business
 * card aspect ratio and printed content inside. Auto-capture only fires after
 * the same quad has been tracked for a while and held still.
 */

/** Width of the downscaled analysis frame; height follows the preview aspect. */
const ANALYSIS_WIDTH = 240;
const MIN_ANALYSIS_HEIGHT = 60;
const MAX_ANALYSIS_HEIGHT = 400;

const SAMPLE_INTERVAL_MS = 125;
const DEBUG_LOG_INTERVAL_MS = 1000;

/** Sobel gradients are clamped so a few very strong pixels cannot fake a line. */
const GRADIENT_CLAMP = 48;
/** Max edge tilt as drift over the line length (tan ~7°). */
const MAX_TILT = 0.12;
const TILT_STEP = 2;
/** Mean signed gradient along a full-length line to become a candidate. */
const MIN_LINE_SCORE = 4;
const MAX_LINE_CANDIDATES = 6;
const LINE_NMS_DISTANCE = 5;

/** Luma step a point on a card edge must show (with the expected polarity). */
const EDGE_MIN_CONTRAST = 16;
/** Ignore the ends of each edge — cards often have rounded corners. */
const EDGE_CORNER_SKIP = 0.1;
const MIN_EDGE_SUPPORT = 0.5;
const MIN_MEAN_EDGE_SUPPORT = 0.7;

/** Card must span a good part of the preview to be readable after capture. */
const MIN_CARD_WIDTH_RATIO = 0.45;
/** ISO 7810 ID-1 is 1.59, DE 85×55 is 1.55, US 3.5×2 is 1.75 — plus tilt. */
const MIN_CARD_ASPECT = 1.35;
const MAX_CARD_ASPECT = 2.05;
/** Opposite edges must have similar lengths (limits perspective skew). */
const MIN_OPPOSITE_EDGE_RATIO = 0.8;

/** Interior must contain print (rejects blank sheets, screens, doors). */
const INTERIOR_INSET = 0.12;
const CONTENT_EDGE_THRESHOLD = 20;
const MIN_CONTENT_DENSITY = 0.015;
const MIN_SHARPNESS = 8;

/** Camera auto-exposure settles during the first frames. */
const WARMUP_MS = 400;
/** Consecutive tracked frames before showing "card detected". */
const DETECTED_STREAK = 2;
/** Consecutive tracked frames before the hold-still countdown starts. */
const HOLD_STREAK = 3;
const STABLE_HOLD_MS = 1000;
/** Average corner movement between frames (fraction of frame width). */
const MAX_QUAD_SHIFT_RATIO = 0.03;
/** Single dropped detections (glare, finger) do not reset tracking. */
const MAX_MISSED_FRAMES = 1;

export interface CardPoint {
  x: number;
  y: number;
}

/** Corners in order top-left, top-right, bottom-right, bottom-left. */
export type CardQuad = [CardPoint, CardPoint, CardPoint, CardPoint];

export interface CardDetection {
  /** Corners in analysis-frame pixels. */
  corners: CardQuad;
  edgeSupport: number;
  contentDensity: number;
  sharpness: number;
  aspect: number;
  widthRatio: number;
  /** 1 = card brighter than background, -1 = darker. */
  polarity: 1 | -1;
}

export type AutoCaptureScanState = 'searching' | 'cardDetected' | 'holdStill';

export interface AutoCaptureAnalysis {
  state: AutoCaptureScanState;
  stableProgress: number;
  shouldCapture: boolean;
  /** Tracked card outline, normalized 0–1 to the visible preview. */
  quad: CardQuad | null;
}

export interface VisibleVideoRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const AUTO_CAPTURE_SAMPLE_INTERVAL_MS = SAMPLE_INTERVAL_MS;

export function isBusinessCardScanDebugEnabled(): boolean {
  return import.meta.env.DEV && env.VITE_DEV_DEBUG === 'true';
}

/**
 * Part of the video frame shown by an `object-cover` element of the given
 * size. On phones the stream is often portrait while the preview is
 * landscape, so most of the frame is cropped away.
 */
export function getVisibleVideoRect(
  videoWidth: number,
  videoHeight: number,
  elementWidth: number,
  elementHeight: number,
): VisibleVideoRect {
  if (elementWidth <= 0 || elementHeight <= 0) {
    return { x: 0, y: 0, width: videoWidth, height: videoHeight };
  }
  const elementAspect = elementWidth / elementHeight;
  const videoAspect = videoWidth / videoHeight;
  if (videoAspect > elementAspect) {
    const width = videoHeight * elementAspect;
    return { x: (videoWidth - width) / 2, y: 0, width, height: videoHeight };
  }
  const height = videoWidth / elementAspect;
  return { x: 0, y: (videoHeight - height) / 2, width: videoWidth, height };
}

function computeGray(data: Uint8ClampedArray, pixelCount: number) {
  const gray = new Float32Array(pixelCount);
  for (let i = 0, p = 0; p < pixelCount; i += 4, p += 1) {
    gray[p] = data[i]! * 0.299 + data[i + 1]! * 0.587 + data[i + 2]! * 0.114;
  }
  return gray;
}

/** Sobel gradients scaled so a step edge of height h yields ~h. */
function computeGradients(gray: Float32Array, width: number, height: number) {
  const gx = new Float32Array(width * height);
  const gy = new Float32Array(width * height);
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const i = y * width + x;
      const tl = gray[i - width - 1]!;
      const t = gray[i - width]!;
      const tr = gray[i - width + 1]!;
      const l = gray[i - 1]!;
      const r = gray[i + 1]!;
      const bl = gray[i + width - 1]!;
      const b = gray[i + width]!;
      const br = gray[i + width + 1]!;
      gx[i] = (tr + 2 * r + br - tl - 2 * l - bl) / 4;
      gy[i] = (bl + 2 * b + br - tl - 2 * t - tr) / 4;
    }
  }
  return { gx, gy };
}

function clampGradient(gradient: Float32Array): Float32Array {
  const clamped = new Float32Array(gradient.length);
  for (let i = 0; i < gradient.length; i += 1) {
    const value = gradient[i]!;
    clamped[i] =
      value > GRADIENT_CLAMP
        ? GRADIENT_CLAMP
        : value < -GRADIENT_CLAMP
          ? -GRADIENT_CLAMP
          : value;
  }
  return clamped;
}

interface LineCandidate {
  /** Position where the line enters the frame (x at y=0 or y at x=0). */
  offset: number;
  /** Drift across the full frame length. */
  shift: number;
  /** Mean signed gradient along the line. */
  score: number;
}

interface LineCandidates {
  positive: LineCandidate[];
  negative: LineCandidate[];
}

function lineCenter(line: LineCandidate): number {
  return line.offset + line.shift / 2;
}

function pickCandidates(lines: LineCandidate[]): LineCandidate[] {
  lines.sort((a, b) => Math.abs(b.score) - Math.abs(a.score));
  const picked: LineCandidate[] = [];
  for (const line of lines) {
    const center = lineCenter(line);
    if (
      picked.some(
        (other) => Math.abs(lineCenter(other) - center) < LINE_NMS_DISTANCE,
      )
    ) {
      continue;
    }
    picked.push(line);
    if (picked.length >= MAX_LINE_CANDIDATES) break;
  }
  return picked;
}

/**
 * Projects the signed gradient along every near-axis-aligned line. Card edges
 * keep one sign along their length; text and texture cancel out.
 */
function findLineCandidates(
  gradient: Float32Array,
  width: number,
  height: number,
  vertical: boolean,
): LineCandidates {
  const along = vertical ? height : width;
  const across = vertical ? width : height;
  const maxShift = Math.round(MAX_TILT * along);
  const drift = new Int32Array(along);
  const positive: LineCandidate[] = [];
  const negative: LineCandidate[] = [];

  for (let shift = -maxShift; shift <= maxShift; shift += TILT_STEP) {
    for (let t = 0; t < along; t += 1) {
      drift[t] = Math.round((shift * t) / (along - 1));
    }
    const start = 1 + Math.max(0, -shift);
    const end = across - 2 - Math.max(0, shift);
    for (let offset = start; offset <= end; offset += 1) {
      let sum = 0;
      for (let t = 1; t < along - 1; t += 1) {
        const position = offset + drift[t]!;
        sum += vertical
          ? gradient[t * width + position]!
          : gradient[position * width + t]!;
      }
      const score = sum / (along - 2);
      if (score >= MIN_LINE_SCORE) {
        positive.push({ offset, shift, score });
      } else if (score <= -MIN_LINE_SCORE) {
        negative.push({ offset, shift, score });
      }
    }
  }

  return {
    positive: pickCandidates(positive),
    negative: pickCandidates(negative),
  };
}

function intersect(
  verticalLine: LineCandidate,
  horizontalLine: LineCandidate,
  width: number,
  height: number,
): CardPoint {
  // Vertical: x = a + b·y. Horizontal: y = c + d·x.
  const a = verticalLine.offset;
  const b = verticalLine.shift / (height - 1);
  const c = horizontalLine.offset;
  const d = horizontalLine.shift / (width - 1);
  const x = (a + b * c) / (1 - b * d);
  return { x, y: c + d * x };
}

function distance(p: CardPoint, q: CardPoint): number {
  return Math.hypot(q.x - p.x, q.y - p.y);
}

/** Share of points along an edge whose gradient has the expected sign. */
function edgeSupport(
  gradient: Float32Array,
  width: number,
  height: number,
  from: CardPoint,
  to: CardPoint,
  sign: 1 | -1,
  vertical: boolean,
): number {
  const samples = Math.max(8, Math.round(distance(from, to)));
  let hits = 0;
  let total = 0;
  for (let i = 0; i <= samples; i += 1) {
    const t = i / samples;
    if (t < EDGE_CORNER_SKIP || t > 1 - EDGE_CORNER_SKIP) continue;
    const x = Math.round(from.x + (to.x - from.x) * t);
    const y = Math.round(from.y + (to.y - from.y) * t);
    let best = Number.NEGATIVE_INFINITY;
    for (let d = -1; d <= 1; d += 1) {
      const px = vertical ? x + d : x;
      const py = vertical ? y : y + d;
      if (px < 1 || px > width - 2 || py < 1 || py > height - 2) continue;
      const value = sign * gradient[py * width + px]!;
      if (value > best) best = value;
    }
    total += 1;
    if (best >= EDGE_MIN_CONTRAST) hits += 1;
  }
  return total > 0 ? hits / total : 0;
}

/** Edge density and Laplacian variance inside the card, away from its border. */
function measureInterior(
  gray: Float32Array,
  gx: Float32Array,
  gy: Float32Array,
  width: number,
  corners: CardQuad,
  cardWidth: number,
  cardHeight: number,
): { contentDensity: number; sharpness: number } {
  const [tl, tr, br, bl] = corners;
  const insetX = cardWidth * INTERIOR_INSET;
  const insetY = cardHeight * INTERIOR_INSET;
  const x0 = Math.ceil(Math.max(tl.x, bl.x) + insetX);
  const x1 = Math.floor(Math.min(tr.x, br.x) - insetX);
  const y0 = Math.ceil(Math.max(tl.y, tr.y) + insetY);
  const y1 = Math.floor(Math.min(bl.y, br.y) - insetY);

  let edges = 0;
  let count = 0;
  let lapSum = 0;
  let lapSumSq = 0;
  for (let y = y0; y <= y1; y += 1) {
    for (let x = x0; x <= x1; x += 1) {
      const i = y * width + x;
      count += 1;
      if (Math.abs(gx[i]!) + Math.abs(gy[i]!) >= CONTENT_EDGE_THRESHOLD) {
        edges += 1;
      }
      const lap =
        4 * gray[i]! -
        gray[i - 1]! -
        gray[i + 1]! -
        gray[i - width]! -
        gray[i + width]!;
      lapSum += lap;
      lapSumSq += lap * lap;
    }
  }
  if (count === 0) return { contentDensity: 0, sharpness: 0 };
  const lapMean = lapSum / count;
  return {
    contentDensity: edges / count,
    sharpness: Math.sqrt(Math.max(0, lapSumSq / count - lapMean * lapMean)),
  };
}

function inFrame(point: CardPoint, width: number, height: number): boolean {
  return (
    point.x >= 0 &&
    point.x <= width - 1 &&
    point.y >= 0 &&
    point.y <= height - 1
  );
}

/**
 * Finds a business card in an RGBA frame, or `null` when none is fully
 * visible. Pure function so it can be tested without a camera.
 */
export function detectBusinessCard(
  data: Uint8ClampedArray,
  width: number,
  height: number,
): CardDetection | null {
  const gray = computeGray(data, width * height);
  const { gx, gy } = computeGradients(gray, width, height);
  const gxClamped = clampGradient(gx);
  const gyClamped = clampGradient(gy);

  const verticalLines = findLineCandidates(gxClamped, width, height, true);
  const horizontalLines = findLineCandidates(gyClamped, width, height, false);

  const minCardWidth = MIN_CARD_WIDTH_RATIO * width;
  const minCardHeight = minCardWidth / MAX_CARD_ASPECT;

  let best: { detection: CardDetection; score: number } | null = null;

  for (const polarity of [1, -1] as const) {
    // gx > 0 = brighter to the right. A bright card has a rising left edge.
    const lefts =
      polarity === 1 ? verticalLines.positive : verticalLines.negative;
    const rights =
      polarity === 1 ? verticalLines.negative : verticalLines.positive;
    const tops =
      polarity === 1 ? horizontalLines.positive : horizontalLines.negative;
    const bottoms =
      polarity === 1 ? horizontalLines.negative : horizontalLines.positive;

    for (const left of lefts) {
      for (const right of rights) {
        if (lineCenter(right) - lineCenter(left) < minCardWidth) continue;
        for (const top of tops) {
          for (const bottom of bottoms) {
            if (lineCenter(bottom) - lineCenter(top) < minCardHeight) {
              continue;
            }

            const corners: CardQuad = [
              intersect(left, top, width, height),
              intersect(right, top, width, height),
              intersect(right, bottom, width, height),
              intersect(left, bottom, width, height),
            ];
            if (!corners.every((corner) => inFrame(corner, width, height))) {
              continue;
            }

            const [tl, tr, br, bl] = corners;
            const topLength = distance(tl, tr);
            const bottomLength = distance(bl, br);
            const leftLength = distance(tl, bl);
            const rightLength = distance(tr, br);
            const cardWidth = (topLength + bottomLength) / 2;
            const cardHeight = (leftLength + rightLength) / 2;
            const aspect = cardWidth / cardHeight;

            if (cardWidth < minCardWidth) continue;
            if (aspect < MIN_CARD_ASPECT || aspect > MAX_CARD_ASPECT) continue;
            if (
              Math.min(topLength, bottomLength) /
                Math.max(topLength, bottomLength) <
                MIN_OPPOSITE_EDGE_RATIO ||
              Math.min(leftLength, rightLength) /
                Math.max(leftLength, rightLength) <
                MIN_OPPOSITE_EDGE_RATIO
            ) {
              continue;
            }

            const supports = [
              edgeSupport(gx, width, height, tl, bl, polarity, true),
              edgeSupport(gx, width, height, tr, br, -polarity as 1 | -1, true),
              edgeSupport(gy, width, height, tl, tr, polarity, false),
              edgeSupport(
                gy,
                width,
                height,
                bl,
                br,
                -polarity as 1 | -1,
                false,
              ),
            ];
            const meanSupport =
              supports.reduce((sum, value) => sum + value, 0) / 4;
            if (
              Math.min(...supports) < MIN_EDGE_SUPPORT ||
              meanSupport < MIN_MEAN_EDGE_SUPPORT
            ) {
              continue;
            }

            const widthRatio = cardWidth / width;
            const score = meanSupport * (0.75 + 0.25 * widthRatio);
            if (best && score <= best.score) continue;

            const interior = measureInterior(
              gray,
              gx,
              gy,
              width,
              corners,
              cardWidth,
              cardHeight,
            );
            if (interior.contentDensity < MIN_CONTENT_DENSITY) continue;

            best = {
              score,
              detection: {
                corners,
                edgeSupport: meanSupport,
                contentDensity: interior.contentDensity,
                sharpness: interior.sharpness,
                aspect,
                widthRatio,
                polarity,
              },
            };
          }
        }
      }
    }
  }

  return best?.detection ?? null;
}

function averageCornerShift(a: CardQuad, b: CardQuad): number {
  let sum = 0;
  for (let i = 0; i < 4; i += 1) {
    sum += distance(a[i]!, b[i]!);
  }
  return sum / 4;
}

function normalizeQuad(quad: CardQuad, width: number, height: number) {
  return quad.map((point) => ({
    x: point.x / (width - 1),
    y: point.y / (height - 1),
  })) as CardQuad;
}

export class BusinessCardAutoCaptureAnalyzer {
  private startedAt: number | null = null;

  private stableSince: number | null = null;

  private tracked: CardDetection | null = null;

  private streak = 0;

  private misses = 0;

  private lastDebugLogAt = 0;

  private lastLoggedState: AutoCaptureScanState | null = null;

  reset(): void {
    this.startedAt = null;
    this.stableSince = null;
    this.tracked = null;
    this.streak = 0;
    this.misses = 0;
    this.lastDebugLogAt = 0;
    this.lastLoggedState = null;
  }

  analyze(
    video: HTMLVideoElement,
    canvas: HTMLCanvasElement,
    now: number,
  ): AutoCaptureAnalysis {
    if (!video.videoWidth || !video.videoHeight) {
      this.reset();
      return searchingResult();
    }

    const visible = getVisibleVideoRect(
      video.videoWidth,
      video.videoHeight,
      video.clientWidth,
      video.clientHeight,
    );
    const width = ANALYSIS_WIDTH;
    const height = Math.min(
      MAX_ANALYSIS_HEIGHT,
      Math.max(
        MIN_ANALYSIS_HEIGHT,
        Math.round((width * visible.height) / visible.width),
      ),
    );

    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return searchingResult();

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
      video,
      visible.x,
      visible.y,
      visible.width,
      visible.height,
      0,
      0,
      width,
      height,
    );
    const imageData = ctx.getImageData(0, 0, width, height);
    return this.analyzeFrame(imageData.data, width, height, now);
  }

  /** Tracks detections across frames and decides when to auto-capture. */
  analyzeFrame(
    data: Uint8ClampedArray,
    width: number,
    height: number,
    now: number,
  ): AutoCaptureAnalysis {
    if (this.startedAt === null) this.startedAt = now;

    const detection = detectBusinessCard(data, width, height);

    if (detection) {
      const steady =
        this.tracked !== null &&
        averageCornerShift(this.tracked.corners, detection.corners) <=
          MAX_QUAD_SHIFT_RATIO * width;
      this.streak = steady ? this.streak + 1 : 1;
      this.misses = 0;
      this.tracked = detection;
    } else if (this.tracked) {
      this.misses += 1;
      if (this.misses > MAX_MISSED_FRAMES) {
        this.tracked = null;
        this.streak = 0;
      }
    }

    const warmedUp = now - this.startedAt >= WARMUP_MS;
    let state: AutoCaptureScanState = 'searching';
    let stableProgress = 0;
    let shouldCapture = false;

    if (
      this.tracked &&
      warmedUp &&
      this.streak >= HOLD_STREAK &&
      this.tracked.sharpness >= MIN_SHARPNESS
    ) {
      this.stableSince ??= now;
      const elapsed = now - this.stableSince;
      stableProgress = Math.min(1, elapsed / STABLE_HOLD_MS);
      state = 'holdStill';
      // Only fire on a frame where the card is actually visible.
      shouldCapture = detection !== null && elapsed >= STABLE_HOLD_MS;
    } else {
      this.stableSince = null;
      if (this.tracked && this.streak >= DETECTED_STREAK) {
        state = 'cardDetected';
      }
    }

    const quad =
      state !== 'searching' && this.tracked
        ? normalizeQuad(this.tracked.corners, width, height)
        : null;

    if (isBusinessCardScanDebugEnabled()) {
      this.logDebug(now, state, detection, stableProgress, shouldCapture);
    }

    if (shouldCapture) {
      this.reset();
    }

    return { state, stableProgress, shouldCapture, quad };
  }

  private logDebug(
    now: number,
    state: AutoCaptureScanState,
    detection: CardDetection | null,
    stableProgress: number,
    shouldCapture: boolean,
  ): void {
    const stateChanged = state !== this.lastLoggedState;
    const intervalElapsed = now - this.lastDebugLogAt >= DEBUG_LOG_INTERVAL_MS;
    if (!stateChanged && !intervalElapsed && !shouldCapture) return;

    console.debug(`[card-scan] state=${state} detected=${Boolean(detection)}`, {
      streak: this.streak,
      misses: this.misses,
      stableProgress: Number(stableProgress.toFixed(2)),
      edgeSupport: detection ? Number(detection.edgeSupport.toFixed(2)) : null,
      aspect: detection ? Number(detection.aspect.toFixed(2)) : null,
      widthRatio: detection ? Number(detection.widthRatio.toFixed(2)) : null,
      contentDensity: detection
        ? Number(detection.contentDensity.toFixed(3))
        : null,
      sharpness: detection ? Number(detection.sharpness.toFixed(1)) : null,
      polarity: detection?.polarity ?? null,
    });
    this.lastDebugLogAt = now;
    this.lastLoggedState = state;
  }
}

function searchingResult(): AutoCaptureAnalysis {
  return {
    state: 'searching',
    stableProgress: 0,
    shouldCapture: false,
    quad: null,
  };
}
